import { Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogModule,
    MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { CommonModule } from '@angular/common';
import { DebugService } from '../../../services/debug.service';
import { TranslateModule } from '@ngx-translate/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ArticlesService } from 'api/addons/services/articles.service';
import { ArticleResponseType } from 'api/addons/models/article-response-type';
import { ArticleAttributes } from 'api/addons/models/article-attributes';
import { lastValueFrom } from 'rxjs';
import { SessionService } from '@app/core/services/client-services/session-service/session.service';
import { DeliveryNote } from '../../../../../api/models/delivery-note';
import { DeliveryNoteBundlesService } from 'api/services/delivery-note-bundles.service';
import { DebugCreateArticleDialogComponent } from './debug-create-article-dialog.component';

@Component({
    selector: 'app-debug-manage-articles-dialog',
    templateUrl: './debug-manage-articles-dialog.component.html',
    styleUrl: './debug-manage-articles-dialog.component.scss',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatSelectModule,
        MatTableModule,
        MatDialogModule,
        TranslateModule,
        MatProgressSpinnerModule,
        FormsModule,
    ],
})
export class DebugManageArticlesDialogComponent implements OnInit {
    private dialogRef = inject(
        MatDialogRef<DebugManageArticlesDialogComponent>
    );
    private debugService = inject(DebugService);
    private sessionService = inject(SessionService);
    private articlesService = inject(ArticlesService);
    private bundlesService = inject(DeliveryNoteBundlesService);
    private dialog = inject(MatDialog);
    public data: DebugManageArticlesInputData = inject(MAT_DIALOG_DATA);

    selectedDeliveryNote = signal<DeliveryNote | null>(null);
    deliveryNotes = signal<DeliveryNote[]>([]);
    articles: ArticleResponseType[] = [];
    isLoading = signal(false);
    displayedColumns: string[] = [
        'articleNumber',
        'customerArticleNumber',
        'articleOrderNumber',
        'quantity',
        'index',
        'description',
        'actions',
    ];

    ngOnInit(): void {
        this.loadDeliveryNotes();
    }

    private loadDeliveryNotes(): void {
        // Load delivery notes from the bundle passed in data
        if (this.data?.bundleKey) {
            this.isLoading.set(true);
            lastValueFrom(
                this.bundlesService.getBundle({
                    organizationSiteKey:
                        this.sessionService.currentOrganizationKey,
                    deliveryNoteBundleKey: this.data.bundleKey,
                })
            )
                .then(bundle => {
                    this.deliveryNotes.set(bundle.deliveryNotes || []);
                    if (this.debugService.isLoggingEnabled()) {
                        console.log(
                            '[DebugService] Loaded delivery notes:',
                            this.deliveryNotes()
                        );
                    }
                })
                .catch(error => {
                    const errorMsg =
                        error?.error?.message ||
                        error?.message ||
                        'Unknown error';
                    alert(`Error loading delivery notes: ${errorMsg}`);
                    if (this.debugService.isLoggingEnabled()) {
                        console.error(
                            '[DebugService] Error loading delivery notes:',
                            error
                        );
                    }
                })
                .finally(() => {
                    this.isLoading.set(false);
                });
        }
    }

    async loadArticles(): Promise<void> {
        const note = this.selectedDeliveryNote();
        if (!note) {
            return;
        }

        this.isLoading.set(true);
        try {
            const articlesResponse = await lastValueFrom(
                this.articlesService.getDeliveryNoteArticles({
                    organizationSiteKey:
                        this.sessionService.currentOrganizationKey,
                    deliveryNoteKey: note._key,
                })
            );

            this.articles = articlesResponse || [];

            if (this.debugService.isLoggingEnabled()) {
                console.log('[DebugService] Loaded articles:', this.articles);
            }
        } catch (error: any) {
            const errorMsg =
                error?.error?.message || error?.message || 'Unknown error';
            alert(`Error loading articles: ${errorMsg}`);
            if (this.debugService.isLoggingEnabled()) {
                console.error('[DebugService] Error loading articles:', error);
            }
        } finally {
            this.isLoading.set(false);
        }
    }

    toggleCreateArticleForm(): void {
        const dialogRef = this.dialog.open(DebugCreateArticleDialogComponent, {
            width: '90%',
            maxWidth: '600px',
            disableClose: false,
        });

        dialogRef
            .afterClosed()
            .subscribe(async (result: ArticleAttributes | undefined) => {
                if (result) {
                    await this.createArticle(result);
                }
            });
    }

    private async createArticle(newArticle: ArticleAttributes): Promise<void> {
        const note = this.selectedDeliveryNote();
        if (!note) {
            alert('No delivery note selected');
            return;
        }

        this.isLoading.set(true);
        try {
            if (this.debugService.isLoggingEnabled()) {
                console.log('[DebugService] Creating new article:', newArticle);
            }

            await lastValueFrom(
                this.articlesService.postDeliveryNoteArticles({
                    organizationSiteKey:
                        this.sessionService.currentOrganizationKey,
                    deliveryNoteKey: note._key,
                    body: [newArticle],
                })
            );

            alert(`Article created successfully: ${newArticle.articleNumber}`);

            // Reload articles
            await this.loadArticles();

            if (this.debugService.isLoggingEnabled()) {
                console.log('[DebugService] Article created successfully');
            }
        } catch (error: any) {
            const errorMsg =
                error?.error?.message || error?.message || 'Unknown error';
            alert(`Error creating article: ${errorMsg}`);
            if (this.debugService.isLoggingEnabled()) {
                console.error('[DebugService] Error creating article:', error);
            }
        } finally {
            this.isLoading.set(false);
        }
    }

    async onDeleteArticle(article: ArticleResponseType): Promise<void> {
        if (!confirm(`Delete article: ${article.articleNumber}?`)) {
            return;
        }

        const note = this.selectedDeliveryNote();
        if (!note) {
            alert('No delivery note selected');
            return;
        }

        this.isLoading.set(true);
        try {
            if (this.debugService.isLoggingEnabled()) {
                console.log('[DebugService] Deleting article:', article._key);
            }

            await lastValueFrom(
                this.articlesService.removeArticle({
                    organizationSiteKey:
                        this.sessionService.currentOrganizationKey,
                    deliveryNoteKey: note._key,
                    articleKey: article._key,
                })
            );

            alert(`Article deleted successfully: ${article.articleNumber}`);

            // Reload articles
            await this.loadArticles();

            if (this.debugService.isLoggingEnabled()) {
                console.log('[DebugService] Article deleted successfully');
            }
        } catch (error: any) {
            const errorMsg =
                error?.error?.message || error?.message || 'Unknown error';
            alert(`Error deleting article: ${errorMsg}`);
            if (this.debugService.isLoggingEnabled()) {
                console.error('[DebugService] Error deleting article:', error);
            }
        } finally {
            this.isLoading.set(false);
        }
    }

    closeDialog(): void {
        this.dialogRef.close();
    }
}

export interface DebugManageArticlesInputData {
    bundleKey?: string;
}
